/**
 * visitHeader.js — s126 (TestFlight build 92, Dr. Rami 2026-10-31: "Most recent or next booked appointment date should
 * list here", on Case Home's "Your visit" group header).
 *
 * The header used to read ONLY currentCase.appointmentRequest — the INITIAL encounter's request (the data-isolation
 * contract: follow-ups never write appointment_request), so a case with a follow-up booked for Dec 7 still showed its
 * Oct 19 first visit. The header now names, across every Orella appointment row of the case:
 *   1. the NEXT booked visit (earliest row at or after now that is not completed / cancelled / declined), else
 *   2. the MOST RECENT visit (latest completed or past row).
 * Outside visits (the patient's own, PROD-1160) are passed in as `isOutside` and never named. Pure module: the gate
 * check-s126-fixes executes it.
 */
export const VISIT_HEADER_SKIP = ['cancelled', 'canceled', 'declined', 'rejected', 'no_show']

const day = (d) => String(d || '').slice(0, 10)
const hm = (t) => {
  const m = String(t || '').match(/^(\d{1,2}):(\d{2})/)
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : '00:00'
}
export const visitAt = (row) => {
  const d = day(row?.date || row?.confirmedDate || row?.requestedDate)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null
  const at = new Date(`${d}T${hm(row?.time || row?.confirmedTime || row?.requestedTime)}:00`)
  return Number.isNaN(at.getTime()) ? null : at
}

export function pickHeaderVisit(rows, now, outside) {
  const at0 = now instanceof Date ? now : new Date()
  const skipRow = typeof outside === 'function' ? outside : () => false
  const valid = (Array.isArray(rows) ? rows : [])
    .filter((r) => r && !skipRow(r) && !VISIT_HEADER_SKIP.includes(String(r.status || '').toLowerCase()) && visitAt(r))
  const upcoming = valid
    .filter((r) => String(r.status || '').toLowerCase() !== 'completed' && visitAt(r) >= at0)
    .sort((a, b) => visitAt(a) - visitAt(b))
  if (upcoming.length) return { row: upcoming[0], kind: 'next' }
  const past = valid
    .filter((r) => String(r.status || '').toLowerCase() === 'completed' || visitAt(r) < at0)
    .sort((a, b) => visitAt(b) - visitAt(a))
  return past.length ? { row: past[0], kind: 'last' } : null
}

// "Mon, Dec 7" + "9:00 AM" from a row, with the page's own formatters (dateUtils formatDate / formatAppointmentTime).
export function visitParts(row, formatDate, formatTime) {
  const at = visitAt(row)
  if (!at) return { date: '', time: '' }
  const date = formatDate(day(row.date || row.confirmedDate || row.requestedDate), { weekday: 'short', month: 'short', day: 'numeric' })
  const raw = row.time || row.confirmedTime || row.requestedTime
  return { date, time: raw ? formatTime(hm(raw)) : '' }
}
